import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { tokenManager } from "services/client/token-manager";
import { generateGuestTokenAPI } from "feature/authv2/api/auth-api";
import { notify } from "shared/utils/notify";
import { getDeviceId } from "shared/utils/helpers";

/**
 * Guest token is used only for the OTP login flow (send OTP, verify OTP).
 * It is cleared on successful login. For logging in again, a fresh guest token
 * is required; the Login page requests it when the user is unauthenticated.
 *
 * Default `refreshGuestToken()` keeps existing behavior: if access, refresh, or
 * guest cookie is already present, no network call is made.
 * Use `refreshGuestToken({ force: true })` to always call `/identity` and store
 * a new guest token (ignores existing guest; still writes via tokenManager).
 */

export const GuestTokenContext = createContext(null);

export function GuestTokenProvider({ children }) {
  const [isGuestReady, setIsGuestReady] = useState(false);
  const [error, setError] = useState(null);

  const fetchGuestToken = useCallback(async (options = {}) => {
    const force = options.force === true;

    if (!force) {
      if (tokenManager.getAccess() || tokenManager.getRefresh()) {
        setIsGuestReady(true);
        return;
      }
      if (tokenManager.getGuest()) {
        setIsGuestReady(true);
        return;
      }
    } else {
      tokenManager.clearGuest();
    }

    try {
      setError(null);
      let data = {
        appType: "OPS_PORTAL",
        appVersion: 1,
        macId: getDeviceId(),
      };
      const res = await generateGuestTokenAPI(data);

      if (res?.ok && res?.data?.data?.guestToken) {
        // TOKEN_PERSIST_DUMMY: guest token for OTP pre-login (tokenManager)
        tokenManager.setGuest(res.data.data.guestToken);
        setIsGuestReady(true);
      } else {
        const errMsg = "No guest token in response";
        setError(new Error(errMsg));
        notify.error({
          message: "Guest token failed",
          description: errMsg,
          duration: 2,
          placement: "bottomRight",
          showProgress: true,
        });
      }
    } catch (err) {
      setError(err);
      setIsGuestReady(false);
      const description = err?.message || err?.data?.message || "Could not load guest token";
      notify.error({
        message: "Guest token failed",
        description,
        duration: 2,
        placement: "bottomRight",
        showProgress: true,
      });
    }
  }, []);

  useEffect(() => {
    if (tokenManager.getAccess() || tokenManager.getRefresh()) {
      setIsGuestReady(true);
      return;
    }
    if (tokenManager.getGuest()) {
      setIsGuestReady(true);
      return;
    }
    fetchGuestToken();
  }, [fetchGuestToken]);

  const refreshGuestToken = useCallback((opts) => fetchGuestToken(opts ?? {}), [fetchGuestToken]);
  const forceRefreshGuestToken = useCallback(
    () => fetchGuestToken({ force: true }),
    [fetchGuestToken]
  );

  const value = useMemo(
    () => ({
      isGuestReady,
      error,
      /** @param {{ force?: boolean }} [opts] - `force: true` always refetches from `/identity`. */
      refreshGuestToken,
      /** Always clears guest and fetches a new guest token from the API. */
      forceRefreshGuestToken,
    }),
    [isGuestReady, error, refreshGuestToken, forceRefreshGuestToken]
  );

  return <GuestTokenContext.Provider value={value}>{children}</GuestTokenContext.Provider>;
}

export function useGuestToken() {
  const ctx = useContext(GuestTokenContext);
  if (!ctx) {
    throw new Error("useGuestToken must be used within GuestTokenProvider");
  }
  return ctx;
}
